import { createContext, useContext, useEffect, useState } from "react";
import api, { formatApiError, refreshCsrfToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=anon, obj=user
  const [error, setError] = useState("");
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  // Al montar, intentamos resolver la sesión actual contra el backend.
  // Las cookies (httpOnly access_token + csrf_token) viajan automáticamente
  // gracias a withCredentials. Si no hay sesión, /auth/me devuelve 401.
  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => { setUser(r.data); refreshCsrfToken(); })
      .catch(() => setUser(null));
  }, []);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // El backend setea cookies httpOnly + csrf. No persistimos token en JS.
      // Sincronizamos el token CSRF vigente (respaldo si la cookie no es legible).
      await refreshCsrfToken();
      setUser(data.user);
      setJustLoggedIn(true);
      return { ok: true, user: data.user };
    } catch (e) {
      setError(formatApiError(e));
      return { ok: false };
    }
  };

  const consumeJustLoggedIn = () => setJustLoggedIn(false);

  const changePassword = async (current_password, new_password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/change-password", { current_password, new_password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Aunque falle (red caída, sesión ya expirada), seguimos limpiando estado local.
    }
    setUser(null);
    setJustLoggedIn(false);
  };

  const hasRole = (...roles) => {
    const r = user?.roles || (user?.role ? [user.role] : []);
    return roles.some((x) => r.includes(x));
  };
  const isAdmin = () => hasRole("admin");

  return (
    <AuthContext.Provider value={{
      user, error, login, logout, changePassword, refreshMe, hasRole, isAdmin,
      justLoggedIn, consumeJustLoggedIn,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
